import CruciblePiece from './cruciblePiece';
import { MagicCircleConfig } from '../config';
import config from '../config.json';
import * as THREE from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

export class MagicCircle extends CruciblePiece {
  protected _config: MagicCircleConfig;
  protected _speed: number;

  protected _mixer: THREE.AnimationMixer;
  protected _openingAnimation: THREE.AnimationClip;
  protected _scalingAnimation: THREE.AnimationClip;

  protected _innerRingNode: THREE.Object3D;
  protected _middleRingNode: THREE.Object3D;
  protected _outerRingNode: THREE.Object3D;

  constructor() {
    super();
    this._config = config.magicCircle;
    this._speed = 0.05;

    this._mixer = new THREE.AnimationMixer(this.group);
    this._openingAnimation = new THREE.AnimationClip('', 0, []);
    this._scalingAnimation = new THREE.AnimationClip('', 0, []);

    this._innerRingNode = new THREE.Object3D();
    this._outerRingNode = new THREE.Object3D();
    this._middleRingNode = new THREE.Object3D();
  }

  setup(gltf: GLTF) {
    this.group = gltf.scene;
    this.group.scale.set(2, 2, 2);

    this.group.traverse((node: any) => {
      if (node instanceof THREE.Mesh) {
        node.castShadow = true;
        node.receiveShadow = true;
        if (node.material.map) node.material.map.anisotropy = 16;

        if (node.name === this._config.innerRingNode) {
          this._innerRingNode = node;
        }

        if (node.name === this._config.middleRingNode) {
          this._middleRingNode = node;
        }

        if (node.name === this._config.outerRingNode) {
          this._outerRingNode = node;
        }
      }
    });

    gltf.animations.forEach((clip: THREE.AnimationClip) => {
      this._mixer = new THREE.AnimationMixer(this.group);
      if (clip.name === this._config.openingAnimationName) {
        this._openingAnimation = clip;
      } else {
        this._scalingAnimation = clip;
      }
    });
  }

  setColor(color: THREE.Color) {
    const magicCircleMaterial = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 3
    });

    this.group.traverse((node) => {
      if (node instanceof THREE.Mesh) {
        node.material = magicCircleMaterial;
      }
    });
  }

  playKeyFrameAnimations(timeScale: number = 1) {
    const openingAnimationAction = this._mixer.clipAction(
      this._openingAnimation
    );
    openingAnimationAction.paused = false;
    openingAnimationAction.timeScale = timeScale;
    openingAnimationAction.setLoop(THREE.LoopOnce, 1);
    openingAnimationAction.clampWhenFinished = true;

    openingAnimationAction.play();

    const scalingAnimationAction = this._mixer.clipAction(
      this._scalingAnimation
    );
    scalingAnimationAction.paused = false;
    scalingAnimationAction.timeScale = timeScale;
    scalingAnimationAction.setLoop(THREE.LoopOnce, 1);
    scalingAnimationAction.clampWhenFinished = true;

    scalingAnimationAction.play();
  }

  playKeyFrameAnimationsReversed(timeScale: number = 1) {
    const openingAnimationAction = this._mixer.clipAction(
      this._openingAnimation
    );
    openingAnimationAction.paused = false;
    openingAnimationAction.timeScale = -1 * timeScale;
    openingAnimationAction.setLoop(THREE.LoopOnce, 1);
    openingAnimationAction.clampWhenFinished = true;

    openingAnimationAction.play();

    const scalingAnimationAction = this._mixer.clipAction(
      this._scalingAnimation
    );
    scalingAnimationAction.paused = false;
    scalingAnimationAction.timeScale = -1 * timeScale;
    scalingAnimationAction.setLoop(THREE.LoopOnce, 1);
    scalingAnimationAction.clampWhenFinished = true;

    scalingAnimationAction.play();
  }

  animate(timeElapsed: number, screenshotMode?: boolean) {
    this._mixer.update(timeElapsed);

    if (!screenshotMode) {
      this._innerRingNode.rotation.y += this._speed * timeElapsed;
      this._middleRingNode.rotation.y -= this._speed * timeElapsed;
      this._outerRingNode.rotation.y += this._speed * 2 * timeElapsed;
    }
  }
}
